import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createMedicalTreatmentV3,
  deleteMedicalTreatmentV1,
  updateMedicalTreatmentV1,
} from "@/lib/data/requests/matters/medical-treatment";
import { type APIError, type APIResponse, type Matter } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";
import { queryKeys } from "../../static/query-keys";

export const useCreateMedicalTreatment = ({
  showToast = true,
  showSuccessToast,
  showErrorToast,
}: {
  showToast?: boolean;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
} = {}) => {
  const shouldToastSuccess = showSuccessToast ?? showToast;
  const shouldToastError = showErrorToast ?? showToast;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMedicalTreatmentV3>[0]) =>
      createMedicalTreatmentV3(data),
    onError: () => {
      if (shouldToastError) {
        toast.error("Error creating medical treatment");
      }
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (shouldToastSuccess) {
        toast.success("Medical treatment created successfully");
      }
    },
  });
};

export const useUpdateMedicalTreatment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMedicalTreatmentV1>[0]) =>
      updateMedicalTreatmentV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatments: [
                ...oldData.data.medicalTreatments.map((treatment) =>
                  treatment.id === data.id
                    ? { ...treatment, ...data }
                    : treatment,
                ),
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating medical treatment");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      toast.success("Medical treatment updated successfully");
    },
  });
};

export const useDeleteMedicalTreatment = ({
  showToast = true,
  showSuccessToast,
  showErrorToast,
  successMessage = "Medical treatment deleted successfully",
}: {
  showToast?: boolean;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  successMessage?: string;
} = {}) => {
  const shouldToastSuccess = showSuccessToast ?? showToast;
  const shouldToastError = showErrorToast ?? showToast;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMedicalTreatmentV1>[0]) =>
      deleteMedicalTreatmentV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatments: oldData.data.medicalTreatments.filter(
                (treatment) => treatment.id !== data.id,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      if (shouldToastError) {
        toastAPIErrorMessage(error, "Error deleting medical treatment");
      }
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
    onSuccess: () => {
      if (shouldToastSuccess) {
        toast.success(successMessage);
      }
    },
  });
};
